<template>
  <div class="fc-view__create-new-series">
    <div class="create-new-series__selected-fund">
      <div class="create-new-series__selected-fund-name">
        Fund Name: {{ selectedFund.displayName }}
      </div>
    </div>
    <div class="create-new-series__form-wrapper">
      <template v-if="isLoading">
        <div class="create-new-series__loading-spinner">
          <mds-loader></mds-loader>
        </div>
      </template>
      <template v-else>
        <div class="create-new-series__header">
          <label for="create-series-field">
            Enter a strategy name and the share class for the series.
          </label>
        </div>
        <div class="create-new-series__new-series">
          <mds-form @submit.native.prevent="handleNewSeriesSubmit">
            <div class="new-series-body">
              <mds-input
                :id="seriesFields.strategyName.id"
                :placeholder="seriesFields.strategyName.placeholder"
                :label="seriesFields.strategyName.label"
                :error="seriesFields.strategyName.error"
                :error-text="[seriesFields.strategyName.error && seriesFields.strategyName.errorText]"
                v-model.trim="strategyName"
                max-length="255"
                required
              ></mds-input>
              <mds-input
                :id="seriesFields.shareClass.id"
                :placeholder="seriesFields.shareClass.placeholder"
                :label="seriesFields.shareClass.label"
                v-model.trim="shareClass"
                max-length="255"
              ></mds-input>
            </div>
            <div class="new-series-footer">
              <mds-button
                class="create-new-series-button__continue"
                variation="primary"
                type="submit"
                :disabled="!isValidSeriesData"
              >
                Continue
              </mds-button>
            </div>
          </mds-form>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { get, trim } from 'lodash';
import MdsInput from '@mds/input';
import MdsForm from '@mds/form';

import { mapActions, mapState } from 'vuex';
import * as TargetSeries from '@/services/TargetSeries';

export default {
  name: 'create-new-series',
  components: {
    MdsForm,
    MdsInput,
  },
  props: {
    componentData: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      seriesFields: {
        strategyName: {
          id: 'strategyName',
          label: 'Strategy Name',
          placeholder: 'Strategy Name',
          error: false,
          errorText: '',
        },
        shareClass: {
          id: 'shareClass',
          label: 'Share Class',
          placeholder: 'Share Class',
          error: false,
          errorText: '',
        },
      },
      strategyName: '',
      shareClass: '',
    };
  },
  created() {
    this.setInitialData();
    if (this.strategyName === '') {
      this.setIsLoading(true);
      const selectedClientId = get(this.selectedClient, 'id');
      const selectedFundSecId = get(this.selectedFund, 'secID');
      TargetSeries.getStrategyName(selectedClientId, selectedFundSecId)
        .then((seriesData) => {
          this.strategyName = get(seriesData, 'strategyName', '') || '';
        })
        .catch((error) => {
          this.logError({
            data: {
              selectedClient: this.selectedClient,
              selectedFund: this.selectedFund,
            },
            errorFrom: 'CreateNewSeries:lifecycle:created',
            details: {
              ...Object.getOwnPropertyDescriptors(error),
              message: error.message,
            },
          });
        })
        .finally(() => {
          this.setIsLoading(false);
        });
    } else {
      this.setIsLoading(false);
    }
  },
  methods: {
    ...mapActions([
      'logError',
      'setIsLoading',
    ]),
    setInitialData() {
      if (this.hasSelectedSeries
        && this.selectedSeries.isNew
      ) {
        this.strategyName = this.selectedSeries.strategyName;
        this.shareClass = this.selectedSeries.shareClass;
      }
    },
    handleNewSeriesSubmit() {
      if (!this.isValidSeriesName()) {
        this.seriesFields.strategyName.error = true;
        return false;
      }

      this.seriesFields.strategyName.error = false;
      this.$emit('component-action', {
        name: 'selected-new-series-pending',
        newSeries: {
          id: 'new',
          seriesName: this.generatedSeriesName,
          strategyName: this.strategyName,
          shareClass: this.shareClass,
          isNew: true,
          isValid: true,
          belongsToSelectedClient: true,
        },
      });
      return true;
    },
    isValidSeriesName() {
      const charactersLimit = 255;
      if (!this.strategyName) {
        this.seriesFields.strategyName.errorText = 'Enter a strategy name.';
      } else if (this.generatedSeriesName.length > charactersLimit) {
        this.seriesFields.strategyName.errorText = 'The Series Name should not have more than 255 characters.';
      } else {
        this.seriesFields.strategyName.errorText = null;
      }

      if (this.seriesFields.strategyName.errorText) {
        return false;
      }
      return true;
    },
    validateEntries() {
      if (this.isValidSeriesName()) {
        this.seriesFields.strategyName.error = false;
        return true;
      }
      this.seriesFields.strategyName.error = true;
      return false;
    },
  },
  computed: {
    ...mapState({
      isLoading: (state) => state.system.isLoading,
    }),
    selectedFund() {
      return this.componentData.selectedFund || {};
    },
    selectedClient() {
      return this.componentData.selectedClient || {};
    },
    selectedSeries() {
      return this.componentData.selectedSeries || {};
    },
    hasSelectedSeries() {
      return !!this.selectedSeries;
    },
    isValidSeriesData() {
      return !!this.strategyName && !this.seriesFields.strategyName.error;
    },
    generatedSeriesName() {
      return trim(`${this.strategyName} ${this.shareClass}`);
    },
  },
  watch: {
    strategyName() {
      this.validateEntries();
    },
    shareClass() {
      this.validateEntries();
    },
  },
};
</script>

<style lang="scss">
.fc-view__create-new-series {
  margin: calc(#{$mds-space-5-x} * 2);
}

.fc-button__create-new-series {
  box-shadow: none;
}

.create-new-series__form-wrapper {
  height: 170px;
}

.create-new-series__selected-fund {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: $mds-space-4-x;
}

.create-new-series__new-series {
  display: flex;
  flex-direction: column;
}

.create-new-series__header {
  label {
    @include mds-level-4-heading();
  }
}

.new-series-body {
  display: flex;
  justify-content: space-between;
  margin-top: $mds-space-2-and-a-half-x;
  min-height: $fc-main-intr-process-small-forms-min-heigh;
  label{
    flex: 1;
    &:last-child {
      margin-left: $mds-space-2-and-a-half-x;
    }
  }
}
.new-series-footer {
  margin-top: $fc-int-process-margin-box;
  display: flex;
  justify-content: flex-end;

  .create-new-series-button__continue {
    width: 150px;
  }
}
</style>
